import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NATIONAL_ROLES = ["Administrateur national", "Administrateur Site", "Utilisateur", "Directeur"];
const SITE_ADMIN_ROLES = ["Administrateur Site", "Utilisateur", "Directeur"];

export default function AdminUsers() {
  const { user } = useAuth();
  const isNational = user?.role === "Administrateur national";
  const assignableRoles = isNational ? NATIONAL_ROLES : SITE_ADMIN_ROLES;

  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ nom: "", prenom: "", email: "", role: assignableRoles[0], site_principal: user?.site || "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([axios.get(`${API}/users`), axios.get(`${API}/sites`)]);
      setUsers(u.data);
      setSites(s.data);
    } catch (e) {
      toast.error("Impossible de charger les utilisateurs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/users`, {
        ...form,
        site_principal: isNational ? form.site_principal : user?.site,
      });
      toast.success("Utilisateur créé.");
      setOpen(false);
      setForm({ nom: "", prenom: "", email: "", role: assignableRoles[0], site_principal: user?.site || "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la création.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await axios.put(`${API}/users/${u.ID_UTILISATEUR}`, { actif: u.ACTIF !== "Oui" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la mise à jour.");
    }
  };

  const changeRole = async (u, role) => {
    try {
      await axios.put(`${API}/users/${u.ID_UTILISATEUR}`, { role });
      toast.success("Rôle mis à jour.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la mise à jour.");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-black uppercase tracking-tight text-3xl mb-1">Gestion des utilisateurs</h1>
            <p className="text-sm text-muted-foreground">
              {isNational ? "Tous sites — administrateur national" : `Site ${user?.site} uniquement`}
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-button" className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
                <UserPlus className="h-4 w-4" />
                Ajouter un utilisateur
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-user-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase">Nouvel utilisateur</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="prenom">Prénom</Label>
                    <Input id="prenom" data-testid="user-form-prenom" required value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="nom">Nom</Label>
                    <Input id="nom" data-testid="user-form-nom" required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" data-testid="user-form-email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <Label>Rôle</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-form-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {assignableRoles.map((r) => (
                        <SelectItem key={r} value={r} data-testid={`user-form-role-option-${r}`}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isNational && (
                  <div>
                    <Label>Site</Label>
                    <Select value={form.site_principal} onValueChange={(v) => setForm({ ...form, site_principal: v })}>
                      <SelectTrigger data-testid="user-form-site-select">
                        <SelectValue placeholder="Sélectionner un site" />
                      </SelectTrigger>
                      <SelectContent>
                        {sites.map((s) => (
                          <SelectItem key={s.ID_SITE} value={s.ID_SITE}>{s.NOM_SITE} ({s.CODE_SITE})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {!isNational && (
                  <p className="text-xs text-muted-foreground font-mono uppercase">Site : {user?.site} (verrouillé)</p>
                )}
                <Button type="submit" disabled={submitting} data-testid="user-form-submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Chargement…</p>
        ) : (
          <div className="bg-card border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-secondary text-muted-foreground text-xs font-mono uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Nom</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Rôle</th>
                  <th className="text-left px-4 py-3">Site</th>
                  <th className="text-left px-4 py-3">Actif</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.ID_UTILISATEUR} data-testid={`user-row-${u.ID_UTILISATEUR}`} className="border-t border-border/60">
                    <td className="px-4 py-3">{u.PRENOM} {u.NOM}</td>
                    <td className="px-4 py-3 font-mono text-xs">{u.EMAIL}</td>
                    <td className="px-4 py-3">
                      <Select value={u.ROLE} onValueChange={(v) => changeRole(u, v)}>
                        <SelectTrigger data-testid={`user-role-select-${u.ID_UTILISATEUR}`} className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {assignableRoles.map((r) => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                          ))}
                          {!assignableRoles.includes(u.ROLE) && <SelectItem value={u.ROLE}>{u.ROLE}</SelectItem>}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{u.SITE_PRINCIPAL || "—"}</td>
                    <td className="px-4 py-3">
                      <Switch data-testid={`user-active-toggle-${u.ID_UTILISATEUR}`} checked={u.ACTIF === "Oui"} onCheckedChange={() => toggleActive(u)} />
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground text-sm">Aucun utilisateur.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
